/**
 * CSV export hook.
 *
 * The export endpoints stream a CSV response. We can't use a plain
 * <a href="..."> because we need to send the Authorization + X-Org-Slug
 * headers, so we fetch the blob ourselves and trigger a synthetic download.
 *
 * Caveat: large exports (100k+ rows) will be buffered in memory by the
 * browser before the download is triggered. For massive exports we'd need
 * to use the streaming Filesystem APIs or a download token — out of scope.
 */

import { useMutation } from '@tanstack/react-query';
import { useAuthStore } from '@/lib/auth-store';

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api';

export type ExportDomain = 'devices' | 'sessions' | 'users';

export interface ExportOptions {
  domain: ExportDomain;
  query?: Record<string, string | undefined>;
}

async function downloadExport({ domain, query }: ExportOptions): Promise<void> {
  const { accessToken, orgSlug } = useAuthStore.getState();
  if (!accessToken || !orgSlug) throw new Error('Not authenticated');

  const url = new URL(`${API_BASE}/exports/${domain}`, window.location.origin);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v) url.searchParams.set(k, v);
    }
  }

  const res = await fetch(url.pathname + url.search, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'X-Org-Slug': orgSlug,
      Accept: 'text/csv',
    },
  });

  if (!res.ok) {
    const errText = await res.text().catch(() => '');
    throw new Error(`Export failed: ${res.status} ${errText}`);
  }

  // Extract filename from Content-Disposition or fallback
  const disposition = res.headers.get('Content-Disposition') ?? '';
  const match = /filename="([^"]+)"/.exec(disposition);
  const filename = match?.[1] ?? `weremote-${domain}-${new Date().toISOString().slice(0, 10)}.csv`;

  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(blobUrl);
}

export function useExportCsv() {
  return useMutation({
    mutationFn: downloadExport,
  });
}
